'use client'
import React, { useState } from 'react'
import { ChevronLeft } from 'lucide-react'

interface ImageGalleryProps {
  images: string[]
  /** Prefer main photo as the initial hero when present */
  initialIndex?: number
}

const ImageGallery = ({ images, initialIndex = 0 }: ImageGalleryProps) => {
  const safeInitial = Math.min(Math.max(initialIndex, 0), Math.max(images.length - 1, 0))
  const [activeIndex, setActiveIndex] = useState(safeInitial)
  const [previewOpen, setPreviewOpen] = useState(false)

  if (!images || images.length === 0) {
    return (
      <div className="flex w-full flex-col gap-4 lg:w-[400px] lg:shrink-0">
        <div className="flex aspect-square w-full items-center justify-center rounded-lg bg-gray-100 text-sm text-gray-400">
          No image available
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex w-full flex-col gap-4 lg:w-[400px] lg:shrink-0">
        <div
          className="relative aspect-square w-full cursor-pointer overflow-hidden rounded-lg bg-[#F7F7F7]"
          onClick={() => setPreviewOpen(true)}
        >
          <img
            src={images[activeIndex]}
            alt="Ingredient"
            className="h-full w-full object-cover"
          />
        </div>

        {images.length > 1 && (
          <div className="flex gap-3">
            {images.map((img, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`aspect-square w-full max-w-[125px] flex-1 overflow-hidden rounded-md border-[1.5px] transition-all ${
                  activeIndex === index
                    ? 'border-[#85C349]'
                    : 'border-transparent opacity-80 hover:opacity-100'
                }`}
              >
                <img
                  src={img}
                  alt={`View ${index + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {previewOpen && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-white">
          <div className="px-4 pb-2 pt-4">
            <button
              type="button"
              onClick={() => setPreviewOpen(false)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600"
              aria-label="Close preview"
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="flex flex-1 items-center justify-center px-4">
            <img
              src={images[activeIndex]}
              alt="Ingredient preview"
              className="max-h-full max-w-full object-contain"
            />
          </div>

          {images.length > 1 && (
            <div className="flex gap-2 px-4 pb-6 pt-3">
              {images.map((img, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`h-14 w-14 shrink-0 overflow-hidden rounded-md border-2 transition-all ${
                    activeIndex === index
                      ? 'border-[#85C349]'
                      : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default ImageGallery
